// Public profile + handle (Phase 10-3).
//
// `profiles/{uid}` is publicly readable; writes go through Cloud Functions
// (`claimHandle`, `updateProfile`).

import { collection, doc, getDoc, getDocs, query, where, orderBy, limit, startAfter, onSnapshot } from 'firebase/firestore';
import { updateProfile as authUpdateProfile } from 'firebase/auth';
import { db, auth } from '../firebase.js';

const CLAIM_FN_URL = 'https://us-central1-voda-7647c.cloudfunctions.net/claimHandle';
const UPDATE_FN_URL = 'https://us-central1-voda-7647c.cloudfunctions.net/updateProfile';

export const HANDLE_RE = /^[a-z0-9_]{3,20}$/;
export const BIO_MAX = 200;
export const DISPLAY_NAME_MAX = 30;
export const INSTAGRAM_MAX = 30;

// Generic default name created server-side on profile creation. We treat
// it as "user hasn't picked a name" and fall back to @handle for display.
export const DEFAULT_DISPLAY_NAME = 'archelier user';

// Primary identifier shown across the app — *always* `@handle` (Instagram
// 식). displayName 은 부가 정보로 두 슬롯 있는 페이지 (Profile 헤더, follow
// list 행, Account summary) 에서만 부제목으로 노출. handle 은 unique +
// 클릭/공유 가능한 URL 단위라 가장 안정적인 ID.
export function profileLabel(profile) {
  if (!profile || !profile.handle) return '';
  return `@${profile.handle}`;
}
// Same allowed chars as Instagram itself. Used for client-side hint;
// canonical normalization (strip @, https://instagram.com/, etc.) happens
// server-side.
export const INSTAGRAM_RE = /^[a-zA-Z0-9._]{1,30}$/;

async function authedFetch(url, body) {
  const headers = { 'Content-Type': 'application/json' };
  const user = auth.currentUser;
  if (!user) throw new Error('AUTH_REQUIRED');
  try {
    const token = await user.getIdToken();
    headers.Authorization = `Bearer ${token}`;
  } catch (e) {
    console.warn('profile auth token attach failed:', e);
  }
  const response = await fetch(url, { method: 'POST', headers, body: JSON.stringify({ data: body }) });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const err = new Error(errorData.error || 'Request failed');
    err.code = errorData.error;
    err.status = response.status;
    throw err;
  }
  return response.json();
}

export const ProfileService = {
  async getByUid(uid) {
    if (!uid) return null;
    const snap = await getDoc(doc(db, 'profiles', uid));
    return snap.exists() ? { uid: snap.id, ...snap.data() } : null;
  },

  // Resolve a handle (case-insensitive) to a profile. Two reads — handle
  // reverse-index then the profile itself.
  async getByHandle(handle) {
    const normalized = String(handle || '').trim().toLowerCase();
    if (!normalized) return null;
    const handleSnap = await getDoc(doc(db, 'handles', normalized));
    if (!handleSnap.exists()) return null;
    const uid = handleSnap.data().uid;
    return this.getByUid(uid);
  },

  // Batch fetch profiles for a list of uids — used by feed/listing pages
  // that need (handle / displayName / photoURL) for many designs at once.
  // Firestore `in` is capped at 30, so we chunk. Returns a Map(uid → profile).
  async getProfilesByUids(uids) {
    const unique = Array.from(new Set((uids || []).filter(Boolean)));
    if (unique.length === 0) return new Map();
    const result = new Map();
    for (let i = 0; i < unique.length; i += 30) {
      const chunk = unique.slice(i, i + 30);
      const snaps = await Promise.all(chunk.map(uid => getDoc(doc(db, 'profiles', uid))));
      snaps.forEach((s, j) => {
        if (s.exists()) result.set(chunk[j], { uid: chunk[j], ...s.data() });
      });
    }
    return result;
  },

  // Subscribe to a profile by uid (live counts + bio updates).
  subscribeByUid(uid, cb) {
    if (!uid) { cb(null); return () => {}; }
    return onSnapshot(
      doc(db, 'profiles', uid),
      (snap) => cb(snap.exists() ? { uid: snap.id, ...snap.data() } : null),
      () => cb(null),
    );
  },

  // 프로필 그리드에 뿌릴 디자인 — 그 사람이 만든 것 전부 (성공한 것만).
  //
  // 예전엔 isListed==true 로 걸렀다. 공개 피드를 접으면서 그 플래그를 켜는
  // UI ("피드에 올리기") 도 같이 사라졌으므로, 필터를 남겨두면 앞으로 만드는
  // 디자인은 영영 프로필에 안 뜬다. 피드가 없어진 지금 public/private 구분
  // 자체가 의미를 잃었고, 프로필이 유일한 전시 창구다.
  // designs 는 원래도 firestore.rules 에서 `allow read: if true` 라 ID 만 알면
  // 누구나 읽을 수 있었다 — 새로 열리는 데이터가 아니라 노출 지점만 바뀐 것.
  //
  // 페이지 단위다. 예전엔 limit(24) 하나로 끝나 25번째부터 말없이 잘렸다
  // (2026-10 실측 24개 넘는 사용자 6명, 최대 59개). 더 보기는 lastDoc 커서로.
  async getDesignsByUid(uid, { lastDoc = null, pageSize = 24 } = {}) {
    if (!uid) return { designs: [], lastVisible: null, hasMore: false };
    let q = query(
      collection(db, 'designs'),
      where('userId', '==', uid),
      where('status', '==', 'success'),
      orderBy('timestamp', 'desc'),
      limit(pageSize),
    );
    if (lastDoc) q = query(q, startAfter(lastDoc));
    const snap = await getDocs(q);
    return {
      designs: snap.docs.map(d => ({ id: d.id, ...d.data() })),
      lastVisible: snap.docs[snap.docs.length - 1] || null,
      hasMore: snap.docs.length === pageSize,
    };
  },

  async claimHandle(handle) {
    return authedFetch(CLAIM_FN_URL, { handle });
  },

  async updateBio(bio) {
    return authedFetch(UPDATE_FN_URL, { bio });
  },

  async updateInstagram(instagram) {
    return authedFetch(UPDATE_FN_URL, { instagram });
  },

  // 커스텀 스타일 커뮤니티 공개 (STYLE_GALLERY.md opt-out). users 문서는
  // server-only 라 updateProfile 경유. 읽기는 owner 허용이라 직접 읽는다.
  async updateStyleShare(publicFlag) {
    return authedFetch(UPDATE_FN_URL, { styleSharePublic: !!publicFlag });
  },

  async getStyleShare(uid) {
    if (!uid) return true;
    try {
      const snap = await getDoc(doc(db, 'users', uid));
      return snap.data()?.styleSharePublic !== false;
    } catch {
      return true;
    }
  },

  // Persist displayName to profiles/{uid} (server) and mirror to the
  // current Firebase Auth user so future actions (comments, etc.) pick up
  // the new name without a page reload.
  async updateDisplayName(displayName) {
    const trimmed = String(displayName || '').trim().slice(0, DISPLAY_NAME_MAX);
    const result = await authedFetch(UPDATE_FN_URL, { displayName: trimmed });
    if (auth.currentUser) {
      try { await authUpdateProfile(auth.currentUser, { displayName: trimmed || null }); } catch {}
    }
    return result;
  },
};
